import { Info, User, WarningCircle } from "@phosphor-icons/react";
import clsx from "clsx";
import { useLayoutEffect, useState } from "react";

import { LeadLabels } from "features/labels/components";

import RelativeLink from "common/components/RelativeLink";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Lead } from "common/types";

import { useLead } from "../../datahooks";
import LeadSource from "../LeadSource";
import About from "./About";
import AddLeadNote from "./AddLeadNote";
import AddNoteButton from "./AddNoteButton";
import CurrentExperience from "./CurrentExperience";
import LeadHeading from "./LeadHeading";
import LeadNotes from "./LeadNotes";
import LeadNotesList from "./LeadNotesList";
import LeadOf from "./LeadOf";
import LinkedInSubscription from "./LinkedInSubscription";
import MessageLeadButton from "./MessageLeadButton";
import SectionContainer from "./SectionContainer";

//IF initial state is a lead object, we go straight to viewing notes. the data from initial
//lead is used to display the title and the notes
type LeadPreviewProps = {
  mode?: "inbox" | "leadDetails";
  isWithSendMessage?: boolean;
} & (
  | { initialState: Lead; onClose: () => void }
  | { initialState: string; onClose?: never | undefined }
);

export default function LeadPreview({
  initialState,
  mode,
  ...rest
}: LeadPreviewProps) {
  const isDesktop = useTwBreakpoint("lg");

  const isInitialLead = typeof initialState !== "string";
  const leadId = isInitialLead ? initialState.id : initialState;

  const { lead, isLoadingLead, leadError } = useLead({ profileId: leadId });

  const [notesState, setNotesState] = useState<"viewing" | "adding">(
    isInitialLead ? "viewing" : undefined,
  );

  useLayoutEffect(() => {
    setNotesState(isInitialLead ? "viewing" : undefined);
  }, [initialState]);

  if (notesState === "viewing") {
    return (
      <LeadNotes
        lead={isInitialLead ? initialState : lead}
        onBack={() => {
          isInitialLead ? rest.onClose() : setNotesState(undefined);
        }}
      />
    );
  }

  const isLeadDetails = mode === "leadDetails";

  return leadError ? (
    <div className="flex items-center gap-x-2 m-auto text-red-500">
      <WarningCircle weight="fill" size={24} />
      <p className="text-lg-medium">Error loading lead</p>
    </div>
  ) : (
    <>
      <div
        className={clsx(
          "flex pl-16 lg:pl-4 lg:pr-10 pt-3 pr-4 lg:pt-2.5 py-2.5 border-b",
          isLoadingLead ? "border-b-black-200" : "border-b-transparent",
        )}
      >
        <LeadHeading lead={lead} isLoading={isLoadingLead} size="md" />
      </div>
      {lead && !lead.is_lead && (
        <div className="flex px-4 py-2 border-b border-b-black-200">
          <div className="px-4 py-3 flex border rounded-3xl shadow-sm border-yellow-500 text-xs gap-x-2">
            <Info weight="fill" size={16} />
            This person is not a lead yet - some data and options are not
            available
          </div>
        </div>
      )}
      {!isLoadingLead && lead?.is_lead && (
        <div className="flex px-4 gap-x-2.5 py-2 border-b border-b-black-200">
          <RenderIf condition={!isLeadDetails}>
            <Button
              variant="secondary-purple"
              leftIcon={<User weight="fill" />}
              className="flex-1"
              asChild
              size={isDesktop ? "sm" : "md"}
            >
              <RelativeLink to={`leads/${leadId}`} target="_blank">
                See full profile
              </RelativeLink>
            </Button>
          </RenderIf>
          <RenderIf condition={mode !== "inbox"}>
            <MessageLeadButton
              lead={lead}
              isLoading={isLoadingLead}
              size={isLeadDetails && !isDesktop ? "md" : "sm"}
            />
          </RenderIf>
        </div>
      )}
      {lead?.origins && (
        <SectionContainer title="Origin" isColumn={false} hasBorder>
          <LeadSource leadId={leadId} variant="tagMd" />
        </SectionContainer>
      )}
      <LinkedInSubscription
        isLoading={isLoadingLead}
        hasPremium={lead?.premium}
      />
      {lead?.lead_of && <LeadOf leadOf={lead.lead_of} />}
      {lead?.is_lead && (
        <RestrictedComponent visibleFor={["leads_full"]}>
          <SectionContainer isColumn title="Labels" hasBorder>
            <LeadLabels labels={lead.labels} profileId={leadId} align="start" />
          </SectionContainer>
        </RestrictedComponent>
      )}
      <About lead={lead} isLoading={isLoadingLead} hasBorder={isLeadDetails} />
      {!isLeadDetails && lead && (
        <CurrentExperience
          //this is used to reset the selectedExperienceIndex state inside CurrentExperience when the lead changes
          key={lead.id}
          currentExperience={lead.current_experience}
          isLoading={isLoadingLead}
        />
      )}
      {lead?.is_lead && (
        <RestrictedComponent visibleFor={["leads_full"]}>
          <SectionContainer
            isColumn
            title="Notes"
            hasBorder={false}
            titleComponent={
              !isLeadDetails && (
                <Button
                  variant="link-black"
                  onClick={() => setNotesState("viewing")}
                >
                  See all
                </Button>
              )
            }
          >
            {notesState === "adding" ? (
              <AddLeadNote
                profileId={lead.id}
                onDiscard={() => setNotesState(undefined)}
              />
            ) : (
              <AddNoteButton
                onClick={() =>
                  setNotesState(isLeadDetails ? "adding" : "viewing")
                }
              />
            )}
            <LeadNotesList
              notes={isLeadDetails ? lead.notes : lead.notes.slice(0, 1)}
              profileId={lead.id}
            />
          </SectionContainer>
        </RestrictedComponent>
      )}
    </>
  );
}
